<template>
  <div
    class="ehs-data-table-row"
    :class="{
      'ehs-data-table-row__dirty': isDirty,
      'ehs-data-table-row__unedited': !isDirty,
    }"
  >
    <div class="ehs-data-table-row transition-default">
      <div v-if="selectable" class="ehs-data-table-row__select">
        <template v-if="selectable">
          <Checkbox
            @click.stop="$emit('toggle-select', rowData)"
            ref="selectedCheckbox"
            tabindex="-1"
            :value.sync="isSelected"
          />
        </template>
      </div>
      <div
        v-for="(column, i) in columns"
        :key="column.property"
        class="ehs-data-table-row-cell"
        :class="{
          'ehs-data-table-row-cell--fixed-width': !!column.width,
          'ehs-data-table-row-cell--invalid': isCellInvalid(column, rowData),
          [`w-${column.width}`]: column.width !== undefined,
        }"
        :style="`flex-grow: ${!column.width ? column.flex : 0};`"
      >
        <div class="ehs-data-table-row-cell__content">
          <slot :name="`content:${column.property}`" v-bind="rowData">
            <template v-if="editMode">
              <span :tabindex="rowNumber * 100 + i" class="min-w-full">
                <TextField
                  @keydown.tab="onTabClick($event, i)"
                  v-bind:value="rowData[column.property]"
                  v-on:update:value="onValueUpdate($event, rowData, column, column.property)"
                />
              </span>
            </template>
            <template v-else>
              <span>
                {{ $wizer.utils.formatValue(rowData[column.property]) }}
              </span>
            </template>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Checkbox from '@wizer/core/components/checkbox/Checkbox'
import TextField from '@wizer/core/components/text-field/TextField.vue'
import _ from 'lodash'

export default {
  name: 'EHSEditableRow',
  components: {
    Checkbox,
    TextField,
  },
  props: {
    columns: Array,
    rowData: Object,
    editMode: Boolean,
    isLastRow: Boolean,
    rowNumber: Number,
    tabThroughAction: Function,
    validateCell: Function,
    isCellInvalid: Function,
    selectable: Boolean,
  },
  data() {
    return {
      isSelected: false,
      isDirty: this.rowData.rowProps?.isDirty || false,
    }
  },
  mounted() {
    // this ensures we do not focus on `selected for deletion` when navigating with tab key
    const checkbox = this.$refs.selectedCheckbox.$el.querySelector('.w-checkbox__input')
    checkbox.setAttribute('tabindex', '-1')
    this.rowData.dataTableId = _.uniqueId('datatable_')

    // we need to force validations as soon as the component is created
    this.$emit('rowUpdated', true)
  },
  methods: {
    onValueUpdate($event, rowData, column, columnProperty) {
      rowData[columnProperty] = $event
      this.$nextTick(() => {
        this.validateCell(column, rowData)
        this.$emit('rowUpdated', false)
        this.isDirty = true
      })
    },
    onTabClick(event, columnIndex) {
      if (event.shiftKey) {
        return
      }
      if (this.isLastRow && columnIndex + 1 === this.columns.length) {
        this.tabThroughAction()
      }
    },
  },
  watch: {
    columns(newColumns) {
      newColumns.forEach((column) => {
        this.validateCell(column, this.rowData)
        this.$emit('rowUpdated', false)
      })
    },
  },
}
</script>

<style scoped>
.ehs-data-table-row {
  @apply flex flex-1;
}
.ehs-data-table-row-cell {
  @apply flex flex-1 overflow-x-hidden px-4 border-b;
  padding-top: 13px;
  padding-bottom: 13px;
  min-width: 2rem;
}
.ehs-data-table-row-cell.ehs-data-table-row-cell--fixed-width {
  flex: 0 0 auto;
}
.ehs-data-table-row-cell__content {
  @apply flex flex-grow items-center;
}
.ehs-data-table-row__select {
  @apply flex w-12 pl-1 border-b items-center;
}
.ehs-data-table-row.ehs-data-table-row__unedited:hover {
  @apply bg-gray-100;
}
.ehs-data-table-row.ehs-data-table-row__dirty {
  @apply bg-yellow-100;
}
.ehs-data-table-row-cell--invalid .ehs-data-table-row-cell__content {
  @apply border-2 rounded-md border-red-400 border-solid;
}
.ehs-data-table-row.ehs-data-table-row__dirty:hover {
  @apply bg-yellow-200;
}
</style>
